import React from 'react'
import SpecificationsHeader from './SpecificationsHeader'
import SpecificationItem from './SpecificationItem'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { useProductFromQueryParam } from '../../ProductsConfiguration'
import { useModificationAndSpecificationsQuery } from '@src/queries/ConfiguratorModificationsAndSpecifications.generated'
import { TubeProperties } from '../../../../utils/enum'
import NumberOfTubes from '../NumberOfTubes'
import { sortBy } from 'lodash'
import ShippingOptions from './ShippingOptions'
import { Divider, Theme, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Specifications } from '@src/types/graphql-types'
import { TubeProduct } from '../../enum'
import useFormikHelper from '../../useFormikHelper'
import ValidationWarnings from '@src/routes/Configurators/validations/ValidationWarnings'
import Services from './Services'
import { BiTubeProduct } from '../../../BiTube/enum'

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
}))

// Helper function to render specifications
const renderSpecification = (spec: Specifications) => {
  if (
    spec.categoryName !== TubeProperties.tubes &&
    spec.categoryName !== 'tube'
  ) {
    return spec.specificationModifications &&
      spec.specificationModifications.length > 0 ? (
      <SpecificationItem specifications={spec} key={spec.categoryDisplayName} />
    ) : null
  } else {
    return <NumberOfTubes key={spec.categoryDisplayName} />
  }
}

// Helper function to determine category order
const getCategoryOrder = (product: string) => {
  const { Concentration, Scale, Amount, Purification, Format, tubes } =
    TubeProperties
  const {
    Rna,
    EasyOligo,
    Standard,
    PureSimple,
    iscaledna,
    LongOligo,
    Wellred,
    Probe,
    Beacon,
  } = TubeProduct
  const { LightCycler, Duplex, FastDuplex, mirna } = BiTubeProduct

  const productTypeToCategoryOrder = {
    [Rna]: [Scale, Amount, Purification, Format, Concentration, tubes],
    [EasyOligo]: [Amount, Purification, Format, Concentration],
    [Standard]: [Scale, Amount, Purification, Format, Concentration, tubes],
    [PureSimple]: [Amount, Purification, Format],
    [iscaledna]: [Amount, Purification, Format, tubes],
    [LongOligo]: [Purification, Format],
    [Wellred]: [Amount, Purification, Format, tubes],
    [Probe]: [Amount, Purification, Format, tubes],
    [Beacon]: [Amount, Purification, Format, tubes],
    [LightCycler]: [Amount, Purification, Format, 'tube'],
    [Duplex]: [Amount, Purification, Format, 'tube'],
    [FastDuplex]: [Amount, Concentration, Format, Purification, 'tube'],
    [mirna]: [Amount, Purification, Format, 'tube'],
  }

  return productTypeToCategoryOrder[product] || []
}

const SpecificationsHome: React.FC = () => {
  const classes = useStyles()
  const { isCustomerServiceRep, isCustomProductCSR, userIsLoggedIn } =
    useCurrentUser()
  const product = useProductFromQueryParam()
  const formik = useFormikHelper()
  const { data } = useModificationAndSpecificationsQuery({
    variables: {
      product: {
        productType: product,
        isCSR: isCustomerServiceRep,
        isCSRSupervisor: isCustomProductCSR,
        isRegUser: userIsLoggedIn,
      },
    },
    ssr: false,
    fetchPolicy: 'cache-first',
  })

  const specifications =
    data?.getModificationAndSpecifications?.specifications || []

  const shippingSpecifications: Specifications[] = specifications.filter(
    (item) => item.categoryName === TubeProperties.Shipping
  )

  const categoryOrder = getCategoryOrder(product)
  const sortedSpecifications = sortBy(
    specifications.filter((item) => categoryOrder.includes(item.categoryName)),
    (item) => categoryOrder.indexOf(item.categoryName)
  )

  const warningMessages = formik.selectedOligoValues.purificationWarning || []

  return (
    <>
      <SpecificationsHeader />
      <Grid item xs={12}>
        {warningMessages.length > 0 && (
          <Grid container>
            <ValidationWarnings warnings={warningMessages} redesign />
          </Grid>
        )}
      </Grid>
      {sortedSpecifications.map(renderSpecification)}
      {product === TubeProduct.iscaledna && <Services />}
      {shippingSpecifications.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <ShippingOptions specifications={shippingSpecifications} />
        </>
      )}
    </>
  )
}

export default SpecificationsHome
